import { Badge } from "@/components/ui/badge";
import type { InsightsAskResult } from "./InsightsQueryPanel";
import { InsightsSummarizeThemesButton } from "./InsightsSummarizeThemesButton";
import { InsightsViewSqlButton } from "./InsightsViewSqlButton";

type InsightsResultsToolbarProps = {
  result: InsightsAskResult;
  sql?: string | null;
  isSummarizing?: boolean;
  onSummarizeThemes?: () => void;
};

function summarizeButtonLabel(result: InsightsAskResult): string {
  if (result.summaryError) return "Retry theme summary";
  if (result.summary) return "Regenerate themes";
  return "Summarize themes";
}

function showSummarizeButton(result: InsightsAskResult): boolean {
  return result.rowCount > 0;
}

export function InsightsResultsToolbar({
  result,
  sql,
  isSummarizing = false,
  onSummarizeThemes,
}: InsightsResultsToolbarProps) {
  const badges: { label: string; color?: "gray" | "primary" | "indigo" }[] = [
    {
      label: `${result.rowCount} row${result.rowCount === 1 ? "" : "s"}`,
      color: "gray",
    },
  ];

  if (result.latencyMs) {
    badges.push({ label: `${result.latencyMs}ms SQL`, color: "gray" });
  }
  if (result.summaryLatencyMs) {
    badges.push({
      label: `${result.summaryLatencyMs}ms themes`,
      color: "indigo",
    });
  }

  const summarizeVisible = showSummarizeButton(result);
  const canSummarize = result.canSummarizeRows ?? false;

  return (
    <div className="flex flex-wrap items-center gap-1.5 shrink-0 min-w-0 pb-2">
      {badges.map((badge) => (
        <Badge key={badge.label} color={badge.color ?? "gray"} size="sm">
          {badge.label}
        </Badge>
      ))}
      {summarizeVisible && onSummarizeThemes ? (
        <InsightsSummarizeThemesButton
          label={summarizeButtonLabel(result)}
          onPress={onSummarizeThemes}
          isPending={isSummarizing}
          isDisabled={!canSummarize}
          disabledReason={result.summarizeIneligibleReason}
        />
      ) : null}
      {sql ? <InsightsViewSqlButton sql={sql} /> : null}
    </div>
  );
}
